import React, { useState, useEffect } from 'react';
import { SelectedLocation } from '../types';
import { useLocationSelection } from '../hooks/useLocationSelection';
import { boroughs, neighborhoods } from '../data/locationData';

interface SearchSectionProps {
  onSearch: (locations: SelectedLocation[]) => void;
  showSearchButton?: boolean; // New prop to control button visibility
  buttonText?: string; // Custom button text
  realTimeUpdate?: boolean; // Enable real-time updates
  initialLocations?: SelectedLocation[]; // Pass initial locations
}

const SearchSection: React.FC<SearchSectionProps> = ({ 
  onSearch, 
  showSearchButton = true,
  buttonText = "Search",
  realTimeUpdate = false,
  initialLocations = []
}) => {
  const [selectedCity, setSelectedCity] = useState<string>('');
  const {
    expandedBoroughs,
    selectedLocations,
    toggleBorough,
    handleNeighborhoodToggle,
    isNeighborhoodSelected,
    removeLocation,
    isAtMaxSelection,
  } = useLocationSelection({ maxSelections: 5, initialLocations });

  // Call onSearch immediately when locations change in real-time mode
  useEffect(() => {
    if (realTimeUpdate && selectedLocations.length >= 0) {
      onSearch(selectedLocations);
    }
  }, [selectedLocations, realTimeUpdate, onSearch]);

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    setSelectedCity(e.target.value);
  };

  const handleNeighborhoodClick = (borough: string, neighborhood: string) => {
    handleNeighborhoodToggle(borough, neighborhood);
  };

  const handleSearchClick = () => {
    if (!realTimeUpdate) {
      onSearch(selectedLocations);
    }
  };

  return (
    <div className="space-y-6">
      {/* City Selection */}
      <div>
        <label className="block text-coffee mb-2 font-semibold">
          Select City
        </label>
        <select
          value={selectedCity}
          onChange={handleCityChange}
          className="w-full p-3 rounded-lg bg-white text-coffee border border-coffee focus:border-terracotta focus:outline-none focus:ring-2 focus:ring-terracotta transition-colors"
        >
          <option value="" className="text-gray-800">Choose a city</option>
          <option value="nyc" className="text-gray-800">New York City</option>
          <option value="jersey-city" className="text-gray-800">Jersey City</option>
        </select>
      </div>

      {/* Borough/Area Selection */}
      {selectedCity && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-coffee font-semibold">
              Select Neighborhoods
            </label>
            <span className="text-coffee opacity-80 text-sm">
              {selectedLocations.length}/5 selected
            </span>
          </div>

          {/* Selected Locations Display */}
          {selectedLocations.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-white border border-coffee rounded-lg">
              {selectedLocations.map((location) => (
                <span
                  key={location.id}
                  className="inline-flex items-center gap-1 bg-white border border-coffee text-coffee px-3 py-1 rounded-full text-sm"
                >
                  {location.neighborhood}
                  <button
                    onClick={() => removeLocation(location.id)}
                    className="text-coffee hover:text-terracotta transition-colors"
                    aria-label={`Remove ${location.neighborhood}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Max selection warning */}
          {isAtMaxSelection() && (
            <div className="p-3 bg-yellow-100 border-2 border-yellow-500 rounded-lg">
              <p className="text-yellow-700 text-sm">
                Maximum 5 neighborhoods selected. Remove one to select another.
              </p>
            </div>
          )}

          {/* Borough List */}
          <div className="space-y-2">
            {(selectedCity === 'nyc' ? boroughs : [{ value: 'jersey-city', label: 'Jersey City' }]).map((borough) => (
              <div key={borough.value} className="border-2 border-coffee rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleBorough(borough.value)}
                  className="w-full p-3 bg-white text-coffee text-left hover:bg-cream transition-colors flex justify-between items-center"
                >
                  <span className="font-medium">{borough.label}</span>
                  <span className="text-coffee">
                    {expandedBoroughs.has(borough.value) ? '−' : '+'}
                  </span>
                </button>
                
                {expandedBoroughs.has(borough.value) && (
                  <div className="p-3 bg-cream grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {neighborhoods[borough.value]?.map((neighborhood) => (
                      <button
                        key={neighborhood.value}
                        onClick={() => handleNeighborhoodClick(borough.value, neighborhood.value)}
                        disabled={isAtMaxSelection() && !isNeighborhoodSelected(borough.value, neighborhood.value)}
                        className={`p-2 rounded text-sm text-left transition-all border-2 ${
                          isNeighborhoodSelected(borough.value, neighborhood.value)
                            ? 'bg-terracotta border-terracotta text-white'
                            : isAtMaxSelection()
                            ? 'bg-white border-coffee text-coffee opacity-50 cursor-not-allowed'
                            : 'bg-white border-coffee text-coffee hover:bg-cream'
                        }`}
                      >
                        {neighborhood.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search Button - Only show if showSearchButton is true */}
      {showSearchButton && !realTimeUpdate && selectedLocations.length > 0 && (
        <button
          onClick={handleSearchClick}
          className="w-full bg-terracotta text-white py-3 rounded-lg font-bold hover:bg-[#d89050] transition-all duration-300 border-2 border-coffee shadow-lg"
        >
          {buttonText}
        </button>
      )}
    </div>
  );
};

export default SearchSection;